import dynamic from 'next/dynamic';
import React from 'react';
import Head from 'next/head';
import { replacePlusSymbol } from 'pages/products/[handle]';
import { CategoryLandingPageKey } from '@lambda/apis/src/category/types';
import settings from '@/settings';
import { getShortenCountryName } from '@/lib/use-origin-country';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import Banner from './Banner';
import Section from '@/components/commons/Section';

import BestSellers from './BestSellers';
import ShopByBrand from './ShopByBrand';
import Marketplace from '@/components/commons/Marketplace';
import SubCategoriesSection from './SubCategoriesSection';
import Seo from './Seo';
import Faq from './Faq';
import { CategoryLandingPageProps } from './types';
import TopCategories from './TopCategories';
import { getCollectionMetaContent, getPreTitle } from './helpers';

const RecentReviews = dynamic(
  () => import('@/components/homepage/RecentReviews'),
  {
    ssr: false,
  },
);

const BackToTopButton = dynamic(
  () => import('@/components/collections/BackToTopButton'),
  { ssr: false },
);

const CircularEconomy = dynamic(
  () => import('@/components/homepage/CircularEconomy'),
  { ssr: false },
);

export default function CategoryLandingPage(props: CategoryLandingPageProps) {
  const categoryName =
    props.storeConfig?.[settings.store]?.title ?? props.config.title;

  const categoryTagline =
    props.storeConfig?.[settings.store]?.tagline ?? props.config.tagline;

  const handle = props.handle as CategoryLandingPageKey;

  const pageTitle = `${getPreTitle()} ${replacePlusSymbol(
    categoryName || '',
  )} Products - Best Prices in ${getShortenCountryName()}`;

  return (
    <div>
      <Head>
        <title>{pageTitle}</title>
        <WebsiteSchema />
        <meta
          name="description"
          content={getCollectionMetaContent(categoryName)}
        />
      </Head>
      <BackToTopButton />
      <div className="bg-white text-gray-700">
        <div className="pb-8">
          {/* Banner */}
          <Banner
            tagline={categoryTagline}
            handle={handle}
            categoryWarrantyInfo={props.categoryWarrantyInfo}
          />
          {/* Our Categories */}
          {props.subCategories.length > 0 && (
            <TopCategories
              subCategories={props.subCategories.slice(0, 12)}
              handle={handle}
              title={categoryName}
            />
          )}
          {/* Best Sellers  */}
          {props.offers.length > 0 && (
            <BestSellers
              offers={props.offers}
              title={categoryName}
              name={props.name}
              categoryPk={props.pk}
            />
          )}
          {/* Shop By Brand */}
          {props.options?.brand?.length && props.options.brand.length > 0 && (
            <ShopByBrand
              brands={props.options.brand
                .map((b) => b.key)
                .filter((b) => b !== '?')}
            />
          )}
        </div>
        {/* Marketplace */}
        <Section className="bg-teal-500 pt-8">
          <Marketplace view="category" handle={handle} />
        </Section>
        {/* SubCategories Section */}
        {props.subCategories.length > 0 && (
          <SubCategoriesSection
            subCategories={props.subCategories}
            handle={handle}
          />
        )}
        {/* Frequently Asked Questions */}
        <div className="bg-gray-200">
          <div className="reebelo-container">
            <Faq handle={handle} />
          </div>
        </div>
        {/* Circular Economy */}
        <div className="reebelo-container">
          <Section>
            <CircularEconomy />
          </Section>
        </div>
        {/* Recent Reviews */}
        <div className="bg-gray-200">
          <div className="reebelo-container">
            <Section>
              <RecentReviews />
            </Section>
          </div>
        </div>
        {/* SEO Section */}
        <Seo handle={handle} />
      </div>
    </div>
  );
}
